import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarClock, Flame, ArrowRight, X, ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "@/lib/faro";
import { cleanCourse, seatsLabel, coursesFromSchedule, mergeCourses, isPointsCourse } from "@/lib/courses";
import nextCourseSeed from "@/data/next-course.generated.json";
import configSeed from "@/data/config.generated.json";
import scheduleSeed from "@/data/schedule.generated.json";

const DEFAULT_ROTATE_MS = 2000;
const MIN_ROTATE_MS = 1000;
const MAX_ROTATE_MS = 60000;

function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  if (Number.isNaN(target)) return null;
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds, done: diff === 0 };
}

function TimeCell({ value, label }) {
  const padded = String(value).padStart(2, "0");
  return (
    <div className="flex flex-col items-center min-w-[44px] sm:min-w-[54px]" data-testid={`countdown-${label}`}>
      <div className="font-display font-black text-xl sm:text-2xl leading-none tabular-nums text-[var(--faro-blue-deep)]">
        {padded}
      </div>
      <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-[var(--faro-blue)] mt-0.5">
        {label}
      </div>
    </div>
  );
}

function Countdown({ cd }) {
  return (
    <div className="flex items-center gap-1 shrink-0" data-testid="next-course-countdown">
      {cd.days > 0 && (
        <>
          <TimeCell value={cd.days} label="días" />
          <span className="font-display font-black text-xl opacity-40">:</span>
        </>
      )}
      <TimeCell value={cd.hours} label="hrs" />
      <span className="font-display font-black text-xl opacity-40">:</span>
      <TimeCell value={cd.minutes} label="min" />
      <span className="font-display font-black text-xl opacity-40 hidden sm:inline">:</span>
      <div className="hidden sm:block">
        <TimeCell value={cd.seconds} label="seg" />
      </div>
    </div>
  );
}

function SeatsCta({ course }) {
  const label = typeof course.seats_remaining === "number" ? seatsLabel(course.seats_remaining) : null;
  const seatsLow = typeof course.seats_remaining === "number" && course.seats_remaining >= 1 && course.seats_remaining <= 5;
  return (
    <div className="flex flex-col items-stretch gap-1.5 shrink-0 min-w-[180px]">
      {label && (
        <div
          data-testid="next-course-seats"
          className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider ${
            seatsLow
              ? "bg-[var(--faro-red)] text-white animate-pulse"
              : "bg-[var(--faro-blue-deep)] text-white"
          }`}
        >
          {seatsLow && <Flame size={12} />}
          {label}
        </div>
      )}

      <a
        href={course.cta_href || "#contacto"}
        data-testid="next-course-cta"
        className="inline-flex items-center justify-center gap-1.5 bg-[var(--faro-blue-deep)] text-white font-black uppercase tracking-wider text-[11px] px-4 py-2 rounded-full hover:bg-[var(--faro-red)] transition-colors"
      >
        {course.cta_label || "Reservar mi plaza"} <ArrowRight size={12} />
      </a>
    </div>
  );
}

function CourseRow({ course }) {
  const cd = useCountdown(course.start_date);
  if (!cd) return null;

  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5 flex-1 min-w-0">
      <Countdown cd={cd} />

      {/* Title / course label */}
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className="w-9 h-9 rounded-full bg-[var(--faro-blue-deep)] text-[var(--faro-yellow)] flex items-center justify-center shrink-0">
          <CalendarClock size={18} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-black">Próximo curso</div>
          <div className="font-display font-black text-base sm:text-lg leading-tight truncate" data-testid="next-course-label">
            {course.course_label}
          </div>
          {course.subtitle && (
            <div className="text-xs sm:text-sm font-semibold opacity-80 mt-0.5 truncate">
              {course.subtitle}
            </div>
          )}
          {course.start_date && (
            <time
              dateTime={course.start_date}
              data-testid="next-course-date"
              className="block text-[11px] sm:text-xs font-bold opacity-95 mt-0.5"
            >
              {new Date(course.start_date).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              {" · "}
              {new Date(course.start_date).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })} h
            </time>
          )}
        </div>
      </div>

      <SeatsCta course={course} />
    </div>
  );
}

const DesktopNav = ({ safeIdx, total, onPrev, onNext }) => (
  <div className="hidden sm:flex items-center gap-1" data-testid="next-course-nav">
    <button
      type="button"
      onClick={onPrev}
      aria-label="Curso anterior"
      data-testid="next-course-prev"
      className="w-6 h-6 rounded-full flex items-center justify-center bg-[var(--faro-blue-deep)]/10 hover:bg-[var(--faro-blue-deep)]/20 transition-colors"
    >
      <ChevronLeft size={12} />
    </button>
    <span className="text-[10px] font-black tabular-nums opacity-70 w-8 text-center">
      {safeIdx + 1}/{total}
    </span>
    <button
      type="button"
      onClick={onNext}
      aria-label="Curso siguiente"
      data-testid="next-course-next"
      className="w-6 h-6 rounded-full flex items-center justify-center bg-[var(--faro-blue-deep)]/10 hover:bg-[var(--faro-blue-deep)]/20 transition-colors"
    >
      <ChevronRight size={12} />
    </button>
  </div>
);

const MobileDots = ({ courses, safeIdx, onSelect }) => (
  <div className="sm:hidden pl-[6.5rem] pr-4 pb-2 flex items-center justify-between gap-2" data-testid="next-course-dots-mobile">
    <div className="flex items-center gap-1.5">
      {courses.map((c, i) => (
        <button
          key={`${c.course_label || "curso"}-${c.start_date || i}`}
          type="button"
          onClick={() => onSelect(i)}
          aria-label={`Ver curso ${i + 1}`}
          data-testid={`next-course-dot-${i}`}
          className={`h-1.5 rounded-full transition-all ${
            i === safeIdx ? "w-6 bg-[var(--faro-blue-deep)]" : "w-1.5 bg-[var(--faro-blue-deep)]/30"
          }`}
        />
      ))}
    </div>
    <Link
      to="/proximos-cursos"
      data-testid="next-course-see-all-mobile"
      className="text-[10px] uppercase tracking-[0.15em] font-black text-[var(--faro-blue-deep)] hover:text-[var(--faro-red)]"
    >
      Ver todos →
    </Link>
  </div>
);

function useNextCourses() {
  const [data, setData] = useState(nextCourseSeed && Object.keys(nextCourseSeed).length ? nextCourseSeed : null);
  const [schedule, setSchedule] = useState(scheduleSeed && Object.keys(scheduleSeed).length ? scheduleSeed : null);
  const [labels, setLabels] = useState(configSeed?.course_type_labels || null);
  const [rotateMs, setRotateMs] = useState(DEFAULT_ROTATE_MS);
  const [dismissed, setDismissed] = useState(false);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tick, setTick] = useState(0); // re-evaluate expired courses periodically

  useEffect(() => {
    axios.get(`${API}/next-course`).then((r) => setData(r.data)).catch(() => {});
    axios.get(`${API}/courses/schedule`).then((r) => setSchedule(r.data)).catch(() => {});
    axios.get(`${API}/config`).then((r) => {
      setLabels(r.data?.course_type_labels || null);
      const s = parseInt(r.data?.banner_rotate_seconds, 10);
      if (Number.isFinite(s) && s > 0) {
        setRotateMs(Math.min(MAX_ROTATE_MS, Math.max(MIN_ROTATE_MS, s * 1000)));
      }
    }).catch(() => {});
  }, []);

  // Re-check expired courses every 30s (countdown itself ticks every second)
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const courses = useMemo(() => {
    if (!data) return [];
    const admin = Array.isArray(data.courses) ? data.courses : [];
    const merged = mergeCourses(admin, coursesFromSchedule(schedule, labels));
    const now = Date.now();
    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
    return merged.filter((c) => {
      if (!c || !c.course_label || !c.start_date) return false;
      const t = new Date(c.start_date).getTime();
      if (Number.isNaN(t)) return false;
      if (t <= now) return false; // hide courses whose start_date has passed
      // Points-family courses: only show editions starting within the next 30 days
      if (isPointsCourse(c) && t - now > THIRTY_DAYS_MS) return false;
      return true;
    }).sort((a, b) => new Date(a.start_date) - new Date(b.start_date)).map((c) => cleanCourse(c, labels));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 'tick' re-evaluates the time-based filter every 30s on purpose
  }, [data, schedule, labels, tick]);

  useEffect(() => {
    if (courses.length <= 1 || paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % courses.length), rotateMs);
    return () => clearInterval(t);
  }, [courses.length, paused, rotateMs]);

  return { data, courses, idx, setIdx, setPaused, dismissed, setDismissed };
}

export default function NextCourseBanner() {
  const { data, courses, idx, setIdx, setPaused, dismissed, setDismissed } = useNextCourses();

  if (!data) {
    return (
      <div
        data-testid="next-course-banner-placeholder"
        aria-hidden="true"
        className="w-full border-b-2 border-[var(--faro-blue-deep)]/20 min-h-[229px] sm:min-h-[113px]"
      />
    );
  }
  if (!data.enabled || dismissed || courses.length === 0) return null;
  const safeIdx = idx % courses.length;
  const current = courses[safeIdx];
  const isMulti = courses.length > 1;

  return (
    <motion.aside
      data-testid="next-course-banner"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] border-b-2 border-[var(--faro-blue-deep)] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto pl-[6.5rem] sm:pl-[10rem] pr-4 sm:pr-8 py-3 sm:py-3.5 flex items-stretch gap-3">
        <div className="flex-1 min-w-0 relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={safeIdx}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="w-full"
            >
              <CourseRow course={current} />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex flex-col items-center justify-between gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setDismissed(true)}
            data-testid="next-course-close"
            aria-label="Cerrar aviso"
            className="w-7 h-7 rounded-full flex items-center justify-center bg-[var(--faro-blue-deep)]/10 hover:bg-[var(--faro-blue-deep)]/20 transition-colors"
          >
            <X size={14} />
          </button>

          {isMulti && (
            <DesktopNav
              safeIdx={safeIdx}
              total={courses.length}
              onPrev={() => setIdx((i) => (i - 1 + courses.length) % courses.length)}
              onNext={() => setIdx((i) => (i + 1) % courses.length)}
            />
          )}

          <Link
            to="/proximos-cursos"
            data-testid="next-course-see-all"
            className="hidden sm:inline-flex text-[10px] uppercase tracking-[0.15em] font-black text-[var(--faro-blue-deep)] hover:text-[var(--faro-red)] transition-colors whitespace-nowrap"
          >
            Ver todos
          </Link>
        </div>
      </div>

      {isMulti && <MobileDots courses={courses} safeIdx={safeIdx} onSelect={setIdx} />}
    </motion.aside>
  );
}
